import { Lock, Check, Play, ChevronRight } from "lucide-react";
import { ICON_MAP } from "@/lib/quests";

export function QuestNode({ quest, progress, onClick }) {
  const Icon = ICON_MAP[quest.icon] || Play;
  const status = progress?.status || "locked";
  const isCompleted = status === "completed";
  const isInProgress = status === "in_progress";
  const isUnlocked = status === "unlocked";
  const isLocked = status === "locked";
  const score = progress?.score;

  const stateClasses = isCompleted
    ? "border-amber-400/60 bg-amber-500/[0.06] hover:bg-amber-500/[0.10]"
    : isInProgress
      ? "border-amber-500/50 bg-amber-500/[0.04] hover:bg-amber-500/[0.08] fmq-pulse"
      : isUnlocked
        ? "border-amber-500/30 bg-zinc-950/40 hover:border-amber-400/60 hover:bg-amber-500/[0.04]"
        : "border-zinc-800/80 bg-zinc-950/40 opacity-55 cursor-pointer hover:opacity-70";

  return (
    <button
      data-testid={`quest-node-${quest.id}`}
      data-status={status}
      onClick={() => onClick?.(quest, status)}
      className={`group w-full text-left rounded-xl border p-5 transition-all duration-300 ${stateClasses}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${
            isCompleted
              ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
              : isLocked
                ? "border-zinc-800 bg-zinc-900/60 text-zinc-600"
                : "border-amber-500/30 bg-zinc-950 text-amber-400"
          }`}
        >
          {isLocked ? <Lock className="h-5 w-5" /> : isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
              Quest {String(quest.id).padStart(2, "0")}
            </span>
            {quest.free && (
              <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-emerald-300">
                free
              </span>
            )}
            {isInProgress && (
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-300">
                in progress
              </span>
            )}
          </div>
          <h3 className="mt-1 font-semibold leading-tight text-zinc-100">
            {quest.title}
          </h3>
          <p className="mt-1 text-sm text-zinc-500 line-clamp-2">{quest.subtitle}</p>

          <div className="mt-3 flex items-center justify-between">
            <span className="font-mono text-[11px] text-zinc-500">+{quest.xp} XP</span>
            {isCompleted && score != null ? (
              <span
                className="font-mono text-xs text-amber-400"
                data-testid={`quest-node-${quest.id}-score`}
              >
                score · {Math.round(score)}
              </span>
            ) : isInProgress ? (
              <span className="text-xs text-amber-300 inline-flex items-center gap-1">
                Continue <ChevronRight className="h-3 w-3" />
              </span>
            ) : isLocked ? (
              <span className="text-xs text-zinc-600">Locked</span>
            ) : (
              <span className="text-xs text-amber-400 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                Start <ChevronRight className="h-3 w-3" />
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
